import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';
import type { LendableConnection, LendableState } from './app-lending-flow';

/**
 * The owner's own connections and the tools on each, for the share dialog's lending picker.
 *
 * <p>Its own module rather than part of `app-lending-flow.ts`, deliberately: that file is import-free
 * so a unit test can walk every branch of the selection rules without dragging the data layer — and its
 * transitive ESM-only dependency — into a jest run that would then fail only in CI.
 *
 * <p>`enabled` is the whole point of the hook. Most shares lend nothing, so a dialog that fetched a
 * connection list on open would spend a round trip on a question nobody asked.
 */
export function useLendableConnections(enabled: boolean): LendableState {
  const query = useQuery({
    queryKey: queryKeys.lendableConnections,
    queryFn: fetchLendableConnections,
    enabled,
  });
  if (!enabled) {
    return { status: 'idle' };
  }
  if (query.isError) {
    return { status: 'error' };
  }
  // `isLoading` here means "no data yet", which is what the picker needs — a background refetch of a
  // list already on screen must not flip it back to a spinner.
  if (query.isLoading || !query.data) {
    return { status: 'loading' };
  }
  return { status: 'ready', connections: query.data };
}

async function fetchLendableConnections(): Promise<LendableConnection[]> {
  const res = await apiFetch(`${API_BASE}/api/apps/lendable`, { credentials: 'include' });
  if (!res.ok) {
    // Thrown, not swallowed: the hook turns this into `error`, which the picker renders as "couldn't
    // load your connections" rather than as "you have none". An owner who reads the second when the
    // first is true shares believing there was nothing on offer.
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { connections?: LendableConnection[] };
  return data.connections ?? [];
}
